/**
 * Shared types used across all services.
 */

/** Board member roles in descending privilege order */
export type BoardRole = 'admin' | 'normal' | 'worker' | 'comment-only' | 'read-only';

// ---------------------------------------------------------------------------
// External Email Invite — dedicated invited-registrations table (Decision 6)
// ---------------------------------------------------------------------------

/**
 * TTL (in days) applied to a pending external invited registration.
 * A pending registration self-expires after this window so stale invites
 * never silently grant access. See design Decision 3.
 */
export const EXTERNAL_INVITE_TTL_DAYS = 21;

/**
 * Best-effort per-board cap on live pending external invited registrations.
 * The cap bounds relay-abuse surface; it is intentionally NOT a race-proof
 * invariant (no denormalized counter). See design Decision 6.
 */
export const EXTERNAL_INVITE_CAP_PER_BOARD = 100;

/**
 * One authoritative record per "someone was invited by email but has not
 * joined Flow yet", stored in the dedicated `coreflow-invited-registrations-<env>`
 * table. The record itself is the uniqueness guard — there is no counter item
 * and no separate guard item (design Decision 6).
 *
 * Keys:
 *   PK     = EMAIL#<normalizedEmail>   SK     = BOARD#<boardId>
 *   GSI1PK = BOARD#<boardId>           GSI1SK = EMAIL#<normalizedEmail>
 */
export interface InvitedRegistration {
  readonly PK: string;
  readonly SK: string;
  readonly GSI1PK: string;
  readonly GSI1SK: string;
  readonly entityType: 'InvitedRegistration';
  /** Stable ULID for admin revoke-by-id mapping and log correlation. */
  readonly invitationId: string;
  /** trim + toLowerCase of the admin-supplied email. */
  readonly normalizedEmail: string;
  readonly boardId: string;
  /** Looked up at create via getBoardTitle; may be the empty string. */
  readonly boardTitle: string;
  readonly role: BoardRole;
  readonly status: 'pending';
  readonly invitedVia: 'external-email';
  readonly inviterUserId: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** Epoch seconds; now + EXTERNAL_INVITE_TTL_DAYS. */
  readonly ttl: number;
}

/** Board visibility levels */
export type BoardVisibility = 'private' | 'public';

/** Custom field types */
export type CustomFieldType = 'text' | 'number' | 'date' | 'dropdown' | 'checkbox' | 'currency' | 'stringTemplate';

/** WIP limit mode */
export type WipLimitMode = 'soft' | 'hard';

/** System mode (bootstrap vs configured) */
export type SystemMode = 'bootstrap' | 'configured';

/** User account status */
export type UserStatus = 'active' | 'disabled';

/** User system role */
export type UserRole = 'admin' | 'user';

/**
 * Live Microsoft Entra account status for an MCP key-holder.
 * - 'active'   — account enabled in Entra (excluded from the needs-attention worklist)
 * - 'disabled' — account present but disabled
 * - 'deleted'  — account not resolvable in Entra (Graph 404)
 * - 'unknown'  — status could not be verified (missing email/credentials or Graph error)
 */
export type EntraAccountStatus = 'active' | 'disabled' | 'deleted' | 'unknown';

/**
 * A user who holds one or more MCP API keys, with their live Entra status and
 * key metadata (never the raw key or hash). Used by the admin MCP-key worklist.
 */
export interface AdminMcpKeyHolder {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string;
  readonly entraStatus: EntraAccountStatus;
  readonly keys: readonly {
    readonly keyId: string;
    readonly name: string;
    readonly prefix: string;
    readonly createdAt: string;
  }[];
}

/** Reason a card was archived */
export type ArchivedReason = 'user' | 'list' | 'swimlane';

/** Pagination cursor for list responses */
export interface PaginationCursor {
  readonly nextCursor: string | null;
  readonly prevCursor: string | null;
  readonly hasMore: boolean;
}

/** Paginated response wrapper */
export interface PaginatedResponse<T> {
  readonly data: T[];
  readonly pagination: PaginationCursor;
}

/** Single item response wrapper */
export interface SingleResponse<T> {
  readonly data: T;
}

/** Command acknowledgment response */
export interface CommandResponse {
  readonly commandId: string;
  readonly status: 'accepted';
  readonly correlationId: string;
}

/** Error response */
export interface ErrorResponse {
  readonly error: {
    readonly code: string;
    readonly message: string;
    readonly correlationId: string;
    readonly fields?: Record<string, string>;
  };
}

/** Actor context extracted from JWT */
export interface Actor {
  readonly userId: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: UserRole;
  /**
   * Email from the UNSIGNED JWT decode, normalized (trim + toLowerCase) and
   * populated whenever the `email` claim is present (absent `email` ->
   * undefined). No `email_verified` gate is applied.
   *
   * CONTRACT: this is NEVER an authorization input. It is a selection /
   * diagnostics signal used to decide whether to look up pending invited
   * registrations for this login. The privilege-granting bind re-derives the
   * verified email from a SIGNATURE-VERIFIED token (Decision 7) and binds only
   * against that; no bind or authorization path may read this field as proof of
   * email ownership.
   */
  readonly unverifiedEmailClaim?: string;
}

/** Validation result */
export interface ValidationResult {
  readonly valid: boolean;
  readonly errors: string[];
}

/** Authorization result */
export interface AuthorizationResult {
  readonly authorized: boolean;
  readonly reason?: string;
}
